import React, { MouseEventHandler, useState } from "react";
import { ClassNames, useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import UIcon, { IconProps } from "../../../lib/icons/untitled_ui/UIcon";
import { smoothTransition } from "../../../styles/animations";
import { Flex } from "../../../styles/utility-components";
import Button, { ButtonProps } from "../button/Button";

type Props = {
  link?: string;
  label: string;
  icon?: IconProps["icon"];
  secondaryIcon?: IconProps["icon"];
  primaryIcon?: IconProps["icon"];
  onSecondaryClick?: MouseEventHandler<HTMLButtonElement>;
  onPrimaryClick?: MouseEventHandler<HTMLButtonElement>;
};

export default function HoverAction({
  link,
  label,
  icon,
  secondaryIcon,
  primaryIcon,
  onSecondaryClick,
  onPrimaryClick,
}: Props) {
  const [hovered, setHovered] = useState(false);
  const theme = useTheme();

  return (
    <Container onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <Flex gap={8} alignItems="center">
        {icon && <UIcon icon={icon} color={theme.colors.sysPrimaryDefault} />}
        <StyledLink href={link}>{label}</StyledLink>
      </Flex>
      {hovered && (
        <ClassNames>
          {({ css }) => (
            <Flex alignItems="center">
              {secondaryIcon && (
                <ActionButton
                  buttonType="tertiary"
                  icon={secondaryIcon}
                  onClick={onSecondaryClick}
                  className={css({
                    backgroundColor: theme.colors.sysBackgroundAlternative,
                  })}
                />
              )}
              {primaryIcon && (
                <ActionButton
                  buttonType="tertiary"
                  icon={primaryIcon}
                  onClick={onPrimaryClick}
                  className={css({
                    backgroundColor: theme.colors.sysBackgroundDefault,
                  })}
                />
              )}
            </Flex>
          )}
        </ClassNames>
      )}
    </Container>
  );
}

const Container = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  justifyContent: "space-between",
  height: 32,
  padding: "8px 11px",
  borderRadius: 4,
  transition: smoothTransition,
  backgroundColor: theme.colors.sysBackgroundDefault,
  "&:hover": {
    backgroundColor: theme.colors.sysPrimarySubtle,
  },
}));

const StyledLink = styled.a(({ theme }) => ({
  all: "unset",
  cursor: "pointer",
  display: "block",
  width: "200px",
  color: theme.colors.sysPrimaryDefault,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  "&:hover": {
    color: theme.colors.sysPrimaryHover,
  },
}));

const ActionButton = styled(Button)<ButtonProps<"button">>(({ theme }) => ({
  color: theme.colors.sysTextDefault,
  width: 24,
  height: 24,
  padding: 0,
}));
